import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { IPaginatedResponse } from "muminsmart-utils";
import { apiSlice } from "@/lib/api/apiSlice";
import { useAppDispatch, useAppStore } from "@/lib/hooks";

export interface UseRtkInfiniteListOptions {
  endpoint: string;
  baseParams: Record<string, unknown>;
  skip?: boolean;
  errorMessage?: string;
}

export function useRtkInfiniteList<T extends { id: string }>({
  endpoint,
  baseParams,
  skip = false,
  errorMessage = "Failed to fetch data",
}: UseRtkInfiniteListOptions) {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const [items, setItems] = useState<T[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(!skip);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isFetchingRef = useRef(false);

  const buildQueryArgs = useCallback(
    (nextPage: number) => ({
      endpoint,
      params: { ...baseParams, page: nextPage },
    }),
    [baseParams, endpoint],
  );

  const selectMergedPageData = useCallback(
    (nextPage: number) => {
      if (nextPage <= 1) {
        return null;
      }

      const cachedResult = apiSlice.endpoints.fetchPaginated.select(
        buildQueryArgs(1),
      )(store.getState());

      return cachedResult?.data as IPaginatedResponse<T> | undefined;
    },
    [buildQueryArgs, store],
  );

  const applyPageData = useCallback(
    (data: IPaginatedResponse<T>, nextPage: number) => {
      setItems(data.items ?? []);
      setTotalItems(data.meta.totalItems);
      setPage(nextPage);
      setHasMore(data.meta.currentPage < data.meta.totalPages);
    },
    [],
  );

  const loadPage = useCallback(
    async (nextPage: number, reset = false) => {
      if (skip || isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      const queryArgs = buildQueryArgs(nextPage);

      if (reset) {
        setIsInitialLoading(true);
        setItems([]);
        setTotalItems(0);
        setPage(1);
        setHasMore(false);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const result = await dispatch(
          apiSlice.endpoints.fetchPaginated.initiate(queryArgs, {
            forceRefetch: reset,
          }),
        ).unwrap();

        const mergedData = selectMergedPageData(nextPage);
        applyPageData(
          (mergedData ?? result) as IPaginatedResponse<T>,
          nextPage,
        );
      } catch {
        toast.error(errorMessage);
        if (reset) {
          setItems([]);
          setTotalItems(0);
          setHasMore(false);
        }
      } finally {
        isFetchingRef.current = false;
        setIsInitialLoading(false);
        setIsLoadingMore(false);
      }
    },
    [
      applyPageData,
      buildQueryArgs,
      dispatch,
      errorMessage,
      selectMergedPageData,
      skip,
    ],
  );

  const replaceItem = useCallback((updated: T) => {
    setItems((prev) =>
      prev.map((item) => (item.id === updated.id ? updated : item)),
    );
  }, []);

  const patchItem = useCallback((id: string, patch: Partial<T>) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  }, []);

  return {
    items,
    setItems,
    totalItems,
    page,
    hasMore,
    isInitialLoading,
    isLoadingMore,
    isLoading: isInitialLoading || isLoadingMore,
    loadPage,
    replaceItem,
    patchItem,
  };
}
